// Predefined Skills for rubric tagging
export const SKILLS = [
  // Critical Thinking & Reasoning
  'Critical Thinking',
  'Analytical Reasoning',
  'Logical Reasoning',
  'Problem Decomposition',
  'Pattern Recognition',
  'Decision Making',
  'Systems Thinking',

  // Knowledge & Learning
  'Information Recall',
  'Conceptual Understanding',
  'Knowledge Synthesis',
  'Note-taking',
  'Summarization',
  'Question Formulation',
  'Self-assessment',
  'Metacognition',

  // Communication
  'Written Explanation',
  'Oral Explanation',
  'Argument Construction',
  'Clarity of Expression',
  'Technical Writing',
  'Academic Writing',
  'Citation & Referencing',

  // AI Literacy
  'Prompt Formulation',
  'Evaluating AI Responses',
  'Source Verification',
  'Bias Identification',
  'Ethical AI Usage',

  // Foundational Analysis
  'Reading Comprehension',
  'Mathematical Reasoning',
  'Scientific Observation',
  'Evidence Identification',
  'Comparing & Contrasting',
  'Cause-and-Effect Reasoning',

  // Problem Solving
  'Step-by-step Problem Solving',
  'Applied Problem Solving',
  'Data Interpretation',
  'Model Understanding',
  'Theoretical Application',
  'Quantitative Reasoning',
  'Qualitative Analysis',

  // Research & Advanced Analysis
  'Research Literacy',
  'Advanced Analysis',
  'Method Selection',
  'Hypothesis Evaluation',
  'Critical Literature Review',
  'Cross-domain Integration',
  'Assumption Testing',

  // Original Thinking
  'Original Problem Framing',
  'Theory Development',
  'Research Design',
  'Experimental Reasoning',
  'Scholarly Critique',
  'Novel Insight Generation',

  // STEM-Specific
  'Mathematical Modeling',
  'Algorithmic Thinking',
  'Computational Reasoning',
  'Data Analysis',
  'Experimental Design',
  'Statistical Inference',
  'Debugging & Error Analysis',

  // Humanities-Specific
  'Textual Analysis',
  'Historical Reasoning',
  'Contextual Interpretation',
  'Comparative Analysis',
  'Ethical Reasoning',
  'Theoretical Critique',

  // Business & Economics
  'Market Analysis',
  'Financial Reasoning',
  'Strategic Thinking',
  'Cost-Benefit Analysis',
  'Decision Modeling',
  'Risk Assessment',

  // Health & Sciences
  'Clinical Reasoning',
  'Diagnostic Thinking',
  'Evidence-Based Analysis',
  'Case Interpretation',
  'Ethical Judgment',
];

// Skill categories for grouping in UI
export const SKILL_CATEGORIES: Record<string, string[]> = {
  'Critical Thinking & Reasoning': [
    'Critical Thinking',
    'Analytical Reasoning',
    'Logical Reasoning',
    'Problem Decomposition',
    'Pattern Recognition',
    'Decision Making',
    'Systems Thinking',
  ],
  'Knowledge & Learning': [
    'Information Recall',
    'Conceptual Understanding',
    'Knowledge Synthesis',
    'Note-taking',
    'Summarization',
    'Question Formulation',
    'Self-assessment',
    'Metacognition',
  ],
  'Communication': [
    'Written Explanation',
    'Oral Explanation',
    'Argument Construction',
    'Clarity of Expression',
    'Technical Writing',
    'Academic Writing',
    'Citation & Referencing',
  ],
  'AI Literacy': [
    'Prompt Formulation',
    'Evaluating AI Responses',
    'Source Verification',
    'Bias Identification',
    'Ethical AI Usage',
  ],
  'Foundational Analysis': [
    'Reading Comprehension',
    'Mathematical Reasoning',
    'Scientific Observation',
    'Evidence Identification',
    'Comparing & Contrasting',
    'Cause-and-Effect Reasoning',
  ],
  'Problem Solving': [
    'Step-by-step Problem Solving',
    'Applied Problem Solving',
    'Data Interpretation',
    'Model Understanding',
    'Theoretical Application',
    'Quantitative Reasoning',
    'Qualitative Analysis',
  ],
  'Research & Advanced Analysis': [
    'Research Literacy',
    'Advanced Analysis',
    'Method Selection',
    'Hypothesis Evaluation',
    'Critical Literature Review',
    'Cross-domain Integration',
    'Assumption Testing',
  ],
  'Original Thinking': [
    'Original Problem Framing',
    'Theory Development',
    'Research Design',
    'Experimental Reasoning',
    'Scholarly Critique',
    'Novel Insight Generation',
  ],
  'STEM': [
    'Mathematical Modeling',
    'Algorithmic Thinking',
    'Computational Reasoning',
    'Data Analysis',
    'Experimental Design',
    'Statistical Inference',
    'Debugging & Error Analysis',
  ],
  'Humanities': [
    'Textual Analysis',
    'Historical Reasoning',
    'Contextual Interpretation',
    'Comparative Analysis',
    'Ethical Reasoning',
    'Theoretical Critique',
  ],
  'Business & Economics': [
    'Market Analysis',
    'Financial Reasoning',
    'Strategic Thinking',
    'Cost-Benefit Analysis',
    'Decision Modeling',
    'Risk Assessment',
  ],
  'Health & Sciences': [
    'Clinical Reasoning',
    'Diagnostic Thinking',
    'Evidence-Based Analysis',
    'Case Interpretation',
    'Ethical Judgment',
  ],
};

// Predefined Learning Outcomes
export const LEARNING_OUTCOMES = [
  // Remember & Understand
  'Recall key facts and definitions accurately',
  'Demonstrate basic understanding of concepts',
  'Explain ideas in one\'s own words',
  'Identify key elements within a topic',
  'Follow structured instructions correctly',

  // Apply
  'Apply learned concepts to familiar problems',
  'Apply concepts to real-world examples',
  'Use appropriate methods to solve problems',
  'Execute multi-step solutions accurately',
  'Demonstrate procedural fluency',

  // Analyze
  'Analyze problems by breaking them into parts',
  'Compare multiple approaches or perspectives',
  'Identify relationships, patterns, or trends',
  'Distinguish relevant from irrelevant information',
  'Detect errors or misconceptions',
  'Interpret data, graphs, or evidence correctly',
  'Draw logical conclusions from evidence',

  // Evaluate
  'Justify answers using reasoning or data',
  'Evaluate strengths and weaknesses of solutions',
  'Assess the validity of claims or arguments',

  // Synthesize & Create
  'Synthesize information from multiple sources',
  'Integrate ideas across topics or disciplines',
  'Transfer knowledge to new or unfamiliar contexts',
  'Form coherent explanations from complex inputs',

  // Communication
  'Construct clear and well-supported arguments',
  'Communicate reasoning effectively in writing',
  'Communicate ideas clearly in spoken form',
  'Use appropriate academic or technical language',
  'Cite sources correctly and ethically',

  // Self-Regulation & Growth
  'Demonstrate independent problem-solving ability',
  'Show improved depth of understanding over time',
  'Reflect on learning progress and gaps',
  'Adjust strategies based on feedback',

  // Higher-Order Thinking
  'Design solutions to open-ended problems',
  'Propose alternative or optimized approaches',
  'Make informed decisions under constraints',
  'Formulate meaningful questions or hypotheses',
  'Select appropriate methods or frameworks',
  'Apply theoretical models appropriately',
  'Critically evaluate assumptions and limitations',

  // Advanced Research & Scholarship
  'Critique existing explanations or solutions',
  'Identify gaps in current understanding',
  'Design structured investigations or studies',
  'Analyze experimental or observational results',
  'Interpret findings in context',
  'Generate original insights or interpretations',
  'Develop novel hypotheses or perspectives',
  'Contribute new understanding to a topic',

  // Ethics & Professional Judgment
  'Demonstrate ethical reasoning in decisions',
  'Apply ethical principles to complex scenarios',
  'Recognize bias and limitations in analysis',
];

// Outcome categories (based on Bloom's Taxonomy levels)
export const OUTCOME_CATEGORIES: Record<string, string[]> = {
  'Remember & Understand': [
    'Recall key facts and definitions accurately',
    'Demonstrate basic understanding of concepts',
    'Explain ideas in one\'s own words',
    'Identify key elements within a topic',
    'Follow structured instructions correctly',
  ],
  'Apply': [
    'Apply learned concepts to familiar problems',
    'Apply concepts to real-world examples',
    'Use appropriate methods to solve problems',
    'Execute multi-step solutions accurately',
    'Demonstrate procedural fluency',
  ],
  'Analyze': [
    'Analyze problems by breaking them into parts',
    'Compare multiple approaches or perspectives',
    'Identify relationships, patterns, or trends',
    'Distinguish relevant from irrelevant information',
    'Detect errors or misconceptions',
    'Interpret data, graphs, or evidence correctly',
    'Draw logical conclusions from evidence',
  ],
  'Evaluate': [
    'Justify answers using reasoning or data',
    'Evaluate strengths and weaknesses of solutions',
    'Assess the validity of claims or arguments',
  ],
  'Synthesize & Create': [
    'Synthesize information from multiple sources',
    'Integrate ideas across topics or disciplines',
    'Transfer knowledge to new or unfamiliar contexts',
    'Form coherent explanations from complex inputs',
  ],
  'Communication': [
    'Construct clear and well-supported arguments',
    'Communicate reasoning effectively in writing',
    'Communicate ideas clearly in spoken form',
    'Use appropriate academic or technical language',
    'Cite sources correctly and ethically',
  ],
  'Self-Regulation & Growth': [
    'Demonstrate independent problem-solving ability',
    'Show improved depth of understanding over time',
    'Reflect on learning progress and gaps',
    'Adjust strategies based on feedback',
  ],
  'Higher-Order Thinking': [
    'Design solutions to open-ended problems',
    'Propose alternative or optimized approaches',
    'Make informed decisions under constraints',
    'Formulate meaningful questions or hypotheses',
    'Select appropriate methods or frameworks',
    'Apply theoretical models appropriately',
    'Critically evaluate assumptions and limitations',
  ],
  'Advanced Research & Scholarship': [
    'Critique existing explanations or solutions',
    'Identify gaps in current understanding',
    'Design structured investigations or studies',
    'Analyze experimental or observational results',
    'Interpret findings in context',
    'Generate original insights or interpretations',
    'Develop novel hypotheses or perspectives',
    'Contribute new understanding to a topic',
  ],
  'Ethics & Professional Judgment': [
    'Demonstrate ethical reasoning in decisions',
    'Apply ethical principles to complex scenarios',
    'Recognize bias and limitations in analysis',
  ],
};
